import { useState, useEffect } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "./useAuth";
import { useMarketplaceStore } from "@/lib/stores/useMarketplaceStore";
import {
  IMarketplaceOrder,
  IMarketplaceProduct,
  IPurchasedProduct,
} from "@/domain/interfaces/general.interface";

export function useMarketplaceProducts() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const {
    products,
    purchasedProductIds,
    purchasedProducts,
    setProducts,
    setPurchasedProducts,
    setPurchasedProductIds,
  } = useMarketplaceStore();

  useEffect(() => {
    async function fetchProducts() {
      try {
        const productsRef = collection(
          db,
          "marketplace",
          "current",
          "products",
        );
        const productsQuery = query(productsRef, where("enabled", "==", true));
        const querySnapshot = await getDocs(productsQuery);

        const fetchedProducts = [] as IMarketplaceProduct[];
        querySnapshot.docs.forEach((doc) => {
          const data = doc.data() as IMarketplaceProduct;
          data.id = doc.id;
          fetchedProducts.push(data);
        });

        setProducts(fetchedProducts);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to fetch products"),
        );
      }
    }

    async function fetchPurchasedProducts() {
      if (!user) {
        setPurchasedProductIds([]);
        setPurchasedProducts([]);
        return;
      }

      try {
        const purchasesRef = query(
          collection(
            db,
            "marketplace",
            "current",
            "orders",
            user.uid,
            "orders",
          ),
          where("status", "==", "paid"),
        );
        const querySnapshot = await getDocs(purchasesRef);
        const currentTime = Date.now();
        const validDocs = querySnapshot.docs.filter((doc) => {
          const data = doc.data();
          return data.paidUntil > currentTime;
        });
        const productsMap = new Map<string, IPurchasedProduct>();
        const variants = ["-api", "-indicator", "-widget"];
        const productIds = [] as string[];
        validDocs.forEach((doc) => {
          const order = doc.data() as IMarketplaceOrder;
          order.items?.forEach((item) => {
            // Check if the item ID has a variant suffix
            let baseId = item.id;
            productIds.push(item.id);
            let variant: string | undefined;
            variants?.forEach((v) => {
              if (item.id.endsWith(v)) {
                baseId = item.id.replace(v, "");
                variant = v.split("-")[1];
              }
            });

            const product = productsMap.get(baseId);

            if (!product) {
              productsMap.set(baseId, {
                id: baseId,
                name: item.name,
                variants: variant ? [variant] : [],
                paidUntil: order.paidUntil || 0,
                paymentMethod: order.paymentMethod,
              });
            } else {
              if (variant && !product.variants.includes(variant)) {
                product.variants.push(variant);
              }
            }
          });
        });

        const products = Array.from(productsMap.values());

        setPurchasedProducts(products);
        setPurchasedProductIds(productIds);
      } catch (err) {
        console.error(err);
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to fetch purchased products"),
        );
      }
    }

    Promise.all([fetchProducts(), fetchPurchasedProducts()]).finally(() => {
      setIsLoading(false);
    });
  }, [user, setProducts, setPurchasedProducts, setPurchasedProductIds]);

  return {
    products,
    purchasedProductIds,
    purchasedProducts,
    isLoading,
    error,
  };
}
